/**
 * New onboarding form (canvas OnboardingScreen in edit mode). Same functions, Home's components:
 *   role-template pre-fill, the manager and Salesforce-mirror people pickers (GET /api/directory/users, two
 *   characters minimum, Graph is reached only by the API), the four search-and-select pickers backed by the picker
 *   tables, software + equipment, Teams extension and call queues only when Teams phone is on, and "Skip 72-hr hold"
 *   for a Super Admin only.
 * Validation is the API's own module (../../api/src/requests/validation), run here before the POST and again,
 * authoritatively, on the server; a server 400 is mapped onto the fields it names, and a 409 duplicate becomes a
 * confirm, not an error.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import { ApiError, createOnboarding, getDepartments, getPicker, getRoleTemplates, type PickerKind, type PickerRow, type RoleTemplate } from './api';
import type { App } from './app';
import { checked, field, invalid, newPeople, peoplePicker, radioGroup, switchRow, val, type FormCtx } from './fields';
import {
  addDays,
  centralToday,
  MAX_START_DAYS,
  NEW_OR_TRANSFER,
  ONBOARDING_EQUIPMENT,
  PERSON_TYPES,
  SOFT_MESSAGES,
  SOFTWARE_ITEMS,
  validateOnboarding,
  type FieldError,
  type OnboardingDraft,
} from '../../api/src/requests/validation';

// ---- state ------------------------------------------------------------------------------------------------------

const KINDS: PickerKind[] = ['sharepoint-sites', 'distro-lists', 'shared-mailboxes', 'call-queues'];

const byKind = <T>(make: () => T): Record<PickerKind, T> =>
  Object.fromEntries(KINDS.map((k) => [k, make()])) as Record<PickerKind, T>;

function blank() {
  return {
    ready: false,
    loadError: '',
    departments: [] as string[],
    templates: [] as RoleTemplate[],
    pickers: byKind<PickerRow[]>(() => []),
    templateId: null as number | null,
    templateNote: '',
    personType: '',
    newOrTransfer: '',
    firstName: '',
    lastName: '',
    preferredName: '',
    phoneNumber: '',
    officeLocation: '',
    jobTitle: '',
    department: '',
    startDay: centralToday(),
    hasDirectReports: false,
    salesforceAccess: false,
    teamsPhone: false,
    teamsExtension: '',
    sel: byKind<string[]>(() => []),
    pq: byKind<string>(() => ''),
    popen: byKind<boolean>(() => false),
    software: new Set<string>(),
    equipment: new Set<string>(),
    equipOther: false,
    equipOtherDescription: '',
    forceStart: false,
    manager: newPeople(),
    mirror: newPeople(),
    errors: [] as FieldError[],
    submitting: false,
    submitError: '',
    dup: null as null | { message: string; matches: number[]; hiddenMatches: number },
  };
}

let s = blank();

export function enterForm(app: App): void {
  s = blank();
  // Canvas OnVisible L36-43: a new request defaults the manager to the person filling it in.
  s.manager.sel = { displayName: app.me.name, email: app.me.upn.toLowerCase() };
  void load(app);
}

async function load(app: App): Promise<void> {
  try {
    const [departments, templates, ...pickers] = await Promise.all([
      getDepartments(),
      getRoleTemplates(),
      ...KINDS.map((k) => getPicker(k)),
    ]);
    s.departments = departments.filter((d) => d.active).map((d) => d.name);
    s.templates = templates;
    KINDS.forEach((k, i) => (s.pickers[k] = pickers[i] as PickerRow[]));
    s.ready = true;
  } catch (e) {
    s.loadError = (e as Error).message || 'The form could not be loaded.';
  }
  app.redraw();
}

// ---- helpers ----------------------------------------------------------------------------------------------------

const errorsFor = (field: string) => s.errors.filter((x) => x.field === field).map((x) => x.message);

function touch(app: App, ...fields: string[]): void {
  const before = s.errors.length;
  s.errors = s.errors.filter((x) => !fields.includes(x.field));
  if (s.errors.length !== before) app.redraw();
}

/** What the shared parts (fields.ts) need from this form. */
const cx = (app: App): FormCtx => ({ app, errors: () => s.errors, touch: (...fields) => touch(app, ...fields) });

function draft(confirmDuplicate = false): OnboardingDraft {
  return {
    roleTemplateId: s.templateId,
    personType: s.personType,
    newOrTransfer: s.newOrTransfer,
    firstName: s.firstName,
    lastName: s.lastName,
    preferredName: s.preferredName,
    phoneNumber: s.phoneNumber,
    department: s.department,
    managerEmail: s.manager.sel?.email ?? '',
    jobTitle: s.jobTitle,
    startDay: s.startDay,
    officeLocation: s.officeLocation,
    hasDirectReports: s.hasDirectReports,
    salesforceAccess: s.salesforceAccess,
    salesforceMirrorEmail: s.salesforceAccess ? (s.mirror.sel?.email ?? '') : '',
    teamsPhone: s.teamsPhone,
    teamsExtension: s.teamsPhone ? s.teamsExtension : '',
    sharepointSiteIds: s.sel['sharepoint-sites'],
    distroListIds: s.sel['distro-lists'],
    sharedMailboxIds: s.sel['shared-mailboxes'],
    callQueueIds: s.teamsPhone ? s.sel['call-queues'] : [],
    software: [...s.software],
    equipment: [...s.equipment],
    equipOther: s.equipOther,
    equipOtherDescription: s.equipOther ? s.equipOtherDescription : '',
    forceStart: s.forceStart,
    confirmDuplicate,
  };
}

function text(app: App, id: keyof typeof s & string, label: string, opts: { required?: boolean; hint?: string; type?: string; max?: number } = {}) {
  return field(
    cx(app),
    id,
    label,
    html`<input id=${id} type=${opts.type ?? 'text'} maxlength=${opts.max ?? 255} .value=${s[id] as string}
      aria-invalid=${invalid(cx(app), id)} aria-describedby=${opts.hint ? `${id}-hint` : nothing}
      @input=${(e: Event) => {
        (s as Record<string, unknown>)[id] = val(e);
        touch(app, id);
      }}>`,
    opts,
  );
}

const radios = (app: App, id: 'personType' | 'newOrTransfer', label: string, options: readonly string[]) =>
  radioGroup(cx(app), id, label, options, s[id], (v) => (s[id] = v));

const toggle = (app: App, id: 'hasDirectReports' | 'salesforceAccess' | 'teamsPhone' | 'equipOther' | 'forceStart', label: string, hint?: string) =>
  switchRow(cx(app), label, s[id], (v) => (s[id] = v), hint);

// ---- role template ----------------------------------------------------------------------------------------------

/**
 * Canvas RoleTemplate_Input.OnChange (L550-567) pre-fills department, job title, direct reports, Teams phone and
 * Salesforce access + mirror, and clears the sites and software picks. The template also carries software, call
 * queues, six equipment flags and (since 2d) SharePoint sites; they are pre-filled here too. A blank template job
 * title means "department-wide default" and leaves the typed job title alone. Everything stays editable.
 */
function applyTemplate(app: App, e: Event): void {
  const id = Number(val(e)) || null;
  s.templateId = id;
  // Postgres returns bigserial ids as strings; compare numerically (found live in the 2d proof).
  const t = s.templates.find((x) => Number(x.id) === id);
  if (!t) {
    s.templateNote = '';
    app.redraw();
    return;
  }
  s.department = t.department;
  // A blank job title is the department-wide default (prompt default 7): leave whatever was typed.
  if (t.job_title) s.jobTitle = t.job_title;
  s.hasDirectReports = t.has_direct_reports;
  s.teamsPhone = t.teams_phone;
  s.salesforceAccess = t.salesforce_access;
  s.mirror = newPeople(t.salesforce_mirror_email ? { displayName: t.salesforce_mirror_email, email: t.salesforce_mirror_email } : null);
  // Sites (2d, default 8): the template's sites that still exist in the picker; the canvas cleared them.
  const siteRows = s.pickers['sharepoint-sites'];
  const wantedSites = t.sharepoint_sites.map((x) => x.site_id);
  const foundSites = wantedSites.filter((id) => siteRows.some((r) => r.id === id));
  s.sel['sharepoint-sites'] = foundSites;
  s.software = new Set(t.software);
  const queues = s.pickers['call-queues'];
  const wanted = t.call_queues.map((c) => c.address.toLowerCase());
  const found = queues.filter((q) => wanted.includes((q.resource_account_upn ?? '').toLowerCase()));
  s.sel['call-queues'] = found.map((q) => q.id);
  const flags: [string, boolean][] = [
    ['Business Credit Card', t.credit_card],
    ['Gas Pin', t.gas_pin],
    ['Key Fob', t.key_fob],
    ['Cell Phone', t.cell_phone],
    ['Laptop', t.laptop],
    ['Cubicle Keys', t.cubicle_keys],
  ];
  for (const [item, on] of flags) {
    if (on) s.equipment.add(item);
    else s.equipment.delete(item);
  }
  const missing = wanted.length - found.length;
  const missingSites = wantedSites.length - foundSites.length;
  s.templateNote =
    `${t.title} applied. Access, software and equipment are pre-filled; review and adjust before saving.` +
    (missing > 0 ? ` ${missing} call queue(s) in the template are no longer in the call-queue list.` : '') +
    (missingSites > 0 ? ` ${missingSites} SharePoint site(s) in the template are no longer in the site list.` : '');
  touch(app, 'department', 'jobTitle', 'roleTemplateId');
  app.redraw();
}

// ---- list pickers (sites, distro lists, shared mailboxes, call queues) ------------------------------------------
// (The manager and Salesforce-mirror people pickers are the shared one in fields.ts.)

interface PickerSpec {
  kind: PickerKind;
  field: string;
  label: string;
  placeholder: string;
  /** Canvas Items filter: sites use `in` (contains) on Title / group name; the others StartsWith on label / mail. */
  match(r: PickerRow, q: string): boolean;
  detail(r: PickerRow): string;
}

const lc = (x: string | null | undefined) => (x ?? '').toLowerCase();

const SPECS: Record<PickerKind, PickerSpec> = {
  'sharepoint-sites': {
    kind: 'sharepoint-sites',
    field: 'sharepointSiteIds',
    label: 'SharePoint sites',
    placeholder: 'Select or search SharePoint sites…',
    match: (r, q) => lc(r.label).includes(q) || lc(r.group_name).includes(q),
    detail: (r) => r.site_type ?? '',
  },
  'distro-lists': {
    kind: 'distro-lists',
    field: 'distroListIds',
    label: 'Distribution lists',
    placeholder: 'Select or search distribution lists…',
    match: (r, q) => lc(r.label).startsWith(q) || lc(r.mail).startsWith(q),
    detail: (r) => [r.mail, r.group_type].filter(Boolean).join(' · '),
  },
  'shared-mailboxes': {
    kind: 'shared-mailboxes',
    field: 'sharedMailboxIds',
    label: 'Shared mailboxes',
    placeholder: 'Select or search shared mailboxes…',
    match: (r, q) => lc(r.label).startsWith(q) || lc(r.mail).startsWith(q),
    detail: (r) => r.mail ?? '',
  },
  'call-queues': {
    kind: 'call-queues',
    field: 'callQueueIds',
    label: 'Call queues',
    placeholder: 'Select or search call queues…',
    match: (r, q) => lc(r.label).startsWith(q) || lc(r.resource_account_upn).startsWith(q),
    detail: (r) => r.resource_account_upn ?? '',
  },
};

function listPicker(app: App, kind: PickerKind): TemplateResult {
  const spec = SPECS[kind];
  const id = `pick-${kind}`;
  const rows = s.pickers[kind];
  const chosen = s.sel[kind];
  const q = lc(s.pq[kind].trim());
  const open = s.popen[kind] || q !== '';
  const matches = open ? rows.filter((r) => !chosen.includes(r.id) && (!q || spec.match(r, q))) : [];
  const close = () => {
    s.popen[kind] = false;
    s.pq[kind] = '';
    app.redraw();
  };
  const openAll = () => {
    s.popen[kind] = true;
    app.redraw();
  };
  const control = html`
    ${chosen.length
      ? html`<ul class="sel-chips">
          ${chosen.map((cid) => {
            const r = rows.find((x) => x.id === cid);
            const name = r?.label ?? cid;
            return html`<li class="sel-chip">${name}<button type="button" aria-label="Remove ${name}" @click=${() => {
              s.sel[kind] = s.sel[kind].filter((x) => x !== cid);
              touch(app, spec.field);
              app.redraw();
            }}>×</button></li>`;
          })}
        </ul>`
      : nothing}
    <div class="combo">
      <input id=${id} type="search" placeholder=${spec.placeholder} autocomplete="off" .value=${s.pq[kind]}
        aria-expanded=${open ? 'true' : 'false'} aria-invalid=${invalid(cx(app), spec.field)}
        @focus=${() => {
          if (!s.popen[kind]) {
            s.popen[kind] = true;
            app.redraw();
          }
        }}
        @input=${(e: Event) => {
          s.pq[kind] = val(e);
          s.popen[kind] = true;
          app.redraw();
        }}
        @keydown=${(e: KeyboardEvent) => e.key === 'Escape' && close()}>
      <button type="button" class="combo-toggle" aria-label=${open ? `Close ${spec.label} list` : `Show all ${spec.label}`}
        @click=${() => (open ? close() : openAll())}>${open ? 'Done' : 'Browse'}</button>
    </div>
    ${open
      ? html`<ul class="results" role="listbox" aria-label="${spec.label}">
          ${matches.length ? nothing : html`<li class="none">${rows.length ? 'No matches' : 'The list is empty'}</li>`}
          ${matches.slice(0, 100).map(
            (r) => html`<li><button type="button" role="option" aria-selected="false" @click=${() => {
              s.sel[kind] = [...s.sel[kind], r.id];
              s.pq[kind] = '';
              touch(app, spec.field);
              app.redraw();
              requestAnimationFrame(() => document.getElementById(id)?.focus());
            }}><b>${r.label}</b>${spec.detail(r) ? html`<span>${spec.detail(r)}</span>` : nothing}</button></li>`,
          )}
        </ul>`
      : nothing}`;
  return field(cx(app), id, spec.label, control, { errField: spec.field, wide: true });
}

// ---- submit -----------------------------------------------------------------------------------------------------

async function submit(app: App, confirmDuplicate = false): Promise<void> {
  const d = draft(confirmDuplicate);
  s.submitError = '';
  s.dup = null;
  s.errors = validateOnboarding(d, { today: centralToday(), departments: s.departments }).errors;
  if (s.errors.length) {
    app.redraw();
    requestAnimationFrame(() => document.getElementById('form-errors')?.focus());
    return;
  }
  s.submitting = true;
  app.redraw();
  try {
    const r = await createOnboarding(d);
    app.notify(`Request saved: ${r.displayName}`);
    app.go(`#/requests/${r.id}`);
    return;
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      s.dup = {
        message: String(e.body.message ?? e.message),
        matches: (e.body.matches as number[] | undefined) ?? [],
        hiddenMatches: Number(e.body.hiddenMatches ?? 0),
      };
    } else if (e instanceof ApiError && e.status === 400 && e.fieldErrors.length) {
      s.errors = e.fieldErrors;
      requestAnimationFrame(() => document.getElementById('form-errors')?.focus());
    } else {
      s.submitError = e instanceof ApiError ? e.message : (e as Error).message || 'The request could not be saved.';
    }
  } finally {
    s.submitting = false;
  }
  app.redraw();
}

function duplicateDialog(app: App): TemplateResult {
  if (!s.dup) return html``;
  const dup = s.dup;
  return html`
    <div class="modal-scrim">
      <div class="modal card" role="dialog" aria-modal="true" aria-labelledby="dup-title">
        <h2 id="dup-title">Possible duplicate</h2>
        <p>${dup.message}</p>
        ${dup.matches.length
          ? html`<p class="muted">Existing: ${dup.matches.map(
              (id, i) => html`${i ? ', ' : ''}<a href="#/requests/${id}" target="_blank" rel="noopener">request #${id}</a>`,
            )}</p>`
          : nothing}
        <div class="modal-acts">
          <button type="button" class="btn" @click=${() => submit(app, true)}>Continue</button>
          <button type="button" class="btn ghost" @click=${() => {
            s.dup = null;
            app.redraw();
          }}>Go back</button>
        </div>
      </div>
    </div>`;
}

// ---- view -------------------------------------------------------------------------------------------------------

const EQUIPMENT_LABELS: Record<string, string> = { 'Business Credit Card': 'Credit Card' };

export function formView(app: App): TemplateResult {
  if (s.loadError) {
    return html`<main id="main" class="wrap">
      <p><a class="back" href="#/">← Requests</a></p>
      <div class="notice err" role="alert">${s.loadError}</div>
    </main>`;
  }
  if (!s.ready) {
    return html`<main id="main" class="wrap"><p><a class="back" href="#/">← Requests</a></p><div class="card"><p>Loading the form…</p></div></main>`;
  }
  const today = centralToday();
  return html`
    <main id="main" class="wrap form-page">
      <p><a class="back" href="#/">← Requests</a></p>
      <div class="hello"><h1>New onboarding request</h1></div>

      ${s.errors.length
        ? html`<div class="notice err" id="form-errors" tabindex="-1" role="alert">
            <b>Please fix the following before saving:</b>
            <ul>${s.errors.map((e) => html`<li>${e.message}</li>`)}</ul>
          </div>`
        : nothing}
      ${s.submitError ? html`<div class="notice err" role="alert">${s.submitError}</div>` : nothing}

      <form novalidate @submit=${(e: Event) => {
        e.preventDefault();
        void submit(app);
      }}>
        <section class="card">
          <h3>Role template</h3>
          ${field(
            cx(app),
            'tpl',
            'Start from a role template',
            html`<select id="tpl" @change=${(e: Event) => applyTemplate(app, e)}>
              <option value="" ?selected=${s.templateId == null}>None</option>
              ${s.templates.map((t) => html`<option value=${String(t.id)} ?selected=${s.templateId === Number(t.id)}>${t.title}</option>`)}
            </select>`,
            { hint: 'Optional. A template pre-fills department, job title, access, software and equipment.', errField: 'roleTemplateId' },
          )}
          ${s.templateNote && s.templateId != null ? html`<p class="ok-note">✓ ${s.templateNote}</p>` : nothing}
        </section>

        <section class="card">
          <h3>New hire</h3>
          <div class="grid2">
            ${radios(app, 'personType', 'Person type', PERSON_TYPES)}
            ${radios(app, 'newOrTransfer', 'New or transfer', NEW_OR_TRANSFER)}
            ${text(app, 'firstName', 'First name', { required: true })}
            ${text(app, 'lastName', 'Last name', { required: true })}
            ${text(app, 'preferredName', 'Preferred name')}
            ${text(app, 'phoneNumber', 'Phone number', {
              required: true,
              type: 'tel',
              hint: 'Their personal mobile, 10 digits. It becomes the MFA phone on day one.',
            })}
            ${text(app, 'officeLocation', 'Office')}
          </div>
        </section>

        <section class="card">
          <h3>Job</h3>
          <div class="grid2">
            ${text(app, 'jobTitle', 'Job title', { required: true })}
            ${field(
              cx(app),
              'department',
              'Department',
              html`<select id="department" aria-invalid=${invalid(cx(app), 'department')} @change=${(e: Event) => {
                s.department = val(e);
                touch(app, 'department');
              }}>
                <option value="" ?selected=${!s.department}>Choose a department</option>
                ${s.departments.map((d) => html`<option value=${d} ?selected=${s.department === d}>${d}</option>`)}
              </select>`,
              { required: true },
            )}
            ${peoplePicker(cx(app), 'manager', 'Manager', s.manager, 'managerEmail', true, 'Defaults to you. Search the directory to pick someone else.')}
            ${field(
              cx(app),
              'startDay',
              'Start day',
              html`<input id="startDay" type="date" .value=${s.startDay} max=${addDays(today, MAX_START_DAYS)}
                aria-invalid=${invalid(cx(app), 'startDay')} @input=${(e: Event) => {
                  s.startDay = val(e);
                  touch(app, 'startDay');
                  app.redraw();
                }}>`,
              {
                required: true,
                hint: `No more than ${MAX_START_DAYS} days from today (Central time).`,
                // Soft only (2b open item 5, settled in 2c): no lower bound on either form.
                soft: s.startDay && /^\d{4}-\d{2}-\d{2}$/.test(s.startDay) && s.startDay < today ? SOFT_MESSAGES.startDayPast : undefined,
              },
            )}
          </div>
          ${toggle(app, 'hasDirectReports', 'Has direct reports')}
        </section>

        <section class="card">
          <h3>Access</h3>
          <div class="stack">
            ${toggle(app, 'salesforceAccess', 'Salesforce access')}
            ${s.salesforceAccess
              ? html`<div class="indent">${peoplePicker(cx(app), 'mirror', 'Salesforce mirror user', s.mirror, 'salesforceMirrorEmail', false,
                  'Optional. Their Salesforce profile and permission sets are copied from this person.')}</div>`
              : nothing}
            ${toggle(app, 'teamsPhone', 'Teams phone')}
            ${s.teamsPhone
              ? html`<div class="indent">
                  ${text(app, 'teamsExtension', 'Teams extension', { type: 'text', max: 3, hint: '3 digits, e.g. 187 (blank = next free number)' })}
                  ${listPicker(app, 'call-queues')}
                </div>`
              : nothing}
            ${listPicker(app, 'sharepoint-sites')}
            ${listPicker(app, 'distro-lists')}
            ${listPicker(app, 'shared-mailboxes')}
          </div>
        </section>

        <section class="card">
          <h3>Software and equipment</h3>
          <fieldset class="field checks ${errorsFor('software').length ? 'has-err' : ''}">
            <legend>Software</legend>
            <div class="opts">
              ${SOFTWARE_ITEMS.map(
                (x) => html`<label class="opt"><input type="checkbox" .checked=${s.software.has(x)} @change=${(e: Event) => {
                  checked(e) ? s.software.add(x) : s.software.delete(x);
                  touch(app, 'software');
                }}> ${x}</label>`,
              )}
            </div>
            ${errorsFor('software').map((m) => html`<p class="ferr">${m}</p>`)}
          </fieldset>
          <fieldset class="field checks ${errorsFor('equipment').length ? 'has-err' : ''}">
            <legend>Equipment</legend>
            <div class="opts">
              ${ONBOARDING_EQUIPMENT.map(
                (x) => html`<label class="opt"><input type="checkbox" .checked=${s.equipment.has(x)} @change=${(e: Event) => {
                  checked(e) ? s.equipment.add(x) : s.equipment.delete(x);
                  touch(app, 'equipment');
                }}> ${EQUIPMENT_LABELS[x] ?? x}</label>`,
              )}
              <label class="opt"><input type="checkbox" .checked=${s.equipOther} @change=${(e: Event) => {
                s.equipOther = checked(e);
                app.redraw();
              }}> Other</label>
            </div>
            ${errorsFor('equipment').map((m) => html`<p class="ferr">${m}</p>`)}
          </fieldset>
          ${s.equipOther ? text(app, 'equipOtherDescription', 'Other equipment', { hint: 'What else they need.' }) : nothing}
        </section>

        ${app.me.access.isSuperAdmin
          ? html`<section class="card admin-only">
              <h3>Super Admin</h3>
              ${toggle(app, 'forceStart', 'Skip 72-hr hold', 'Process this request immediately instead of holding it for the 72-hour undo window.')}
            </section>`
          : nothing}

        <div class="form-acts">
          <button class="btn" type="submit" ?disabled=${s.submitting}>${s.submitting ? 'Saving…' : 'Submit request'}</button>
          <a class="btn ghost" href="#/">Cancel</a>
        </div>
      </form>
      ${duplicateDialog(app)}
    </main>`;
}
